import React from 'react';
import { ClientPopulated } from '@api/READ_ONLY/client_api/Api';
import AppAvatar from '@app/components/ui/Avatar';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import { Button, Skeleton, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDate, formatVisitWhen } from './clientCard.format';
import { CardVisit, clientAge, RECENT_WINDOW_DAYS, recentVisitCounts, visitServiceLines } from './clientCard.model';

/** A single figure with its label. Values that are not known render as an em-dash. */
const Figure = ({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: 'bad';
}) => (
  <div className="min-w-0">
    <div className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
    <div
      className="mt-0.5 text-[19px] font-semibold leading-tight tabular-nums"
      style={{ color: tone === 'bad' ? '#b91c1c' : '#0f172a' }}
    >
      {value}
    </div>
    {sub && <div className="mt-0.5 truncate text-[12px] leading-snug text-slate-500">{sub}</div>}
  </div>
);

/**
 * The history is free text of any length, written as several lines. It keeps its line breaks,
 * is clamped to two lines, and the rest is in a tooltip. Blank separator lines are collapsed in
 * the preview only, so they do not spend one of the two visible lines. Whether it overflows is
 * measured on hover, when the layout is settled, rather than kept in state.
 */
const MedicalHistory = ({ text }: { text: string }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const preview = text.replace(/\n\s*\n+/g, '\n').trim();

  const handleOpenChange = (next: boolean) => {
    const el = ref.current;
    setOpen(next && (!el || el.scrollHeight > el.clientHeight + 1));
  };

  return (
    <Tooltip
      title={<span className="block max-h-[60vh] overflow-auto whitespace-pre-wrap">{text}</span>}
      open={open}
      onOpenChange={handleOpenChange}
      mouseEnterDelay={0.7}
      placement="bottomLeft"
      styles={{ root: { maxWidth: 420 } }}
    >
      <div className="mt-1">
        <div
          ref={ref}
          className="text-[13px] leading-snug text-slate-700"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
          }}
        >
          {preview}
        </div>
      </div>
    </Tooltip>
  );
};

type ClientCardHeaderProps = {
  client: ClientPopulated;
  visits: CardVisit[];
  next: CardVisit | null;
  /** `null` when the balance is unknown — loading or failed. Never rendered as 0. */
  balance: number | null;
  /** The client has no billing account yet, which is not the same as a failure. */
  hasNoBillingAccount: boolean;
  isBalanceLoading: boolean;
  /** Billing is admin-only, so for everyone else the figure is not shown at all. */
  canViewBilling: boolean;
  onOpenNext: () => void;
  onOpenBilling: () => void;
  /** Absent when the reader may not edit the record — the button is then not rendered. */
  onEdit?: () => void;
  /** Absent for a reader who has no authoring screen to be sent to. */
  onNewRequest?: () => void;
};

/**
 * Who the client is, and the figures a coordinator opens this page for.
 *
 * Two of them are buttons onto the drawer that explains them; the visit counts are
 * broken down by the panels below, so they stay plain text.
 *
 * There is no note count. Notes are no longer a field of the visits this header is
 * derived from, and a figure counting the page of a stream it does not show would put
 * a number on screen that nothing else on the page agrees with. The notes panels state
 * their own counts, which are the server's.
 */
const ClientCardHeader: React.FC<ClientCardHeaderProps> = ({
  client,
  visits,
  next,
  balance,
  hasNoBillingAccount,
  isBalanceLoading,
  canViewBilling,
  onOpenNext,
  onOpenBilling,
  onEdit,
  onNewRequest,
}) => {
  const { t, i18n } = useTranslation();

  const relativeDayLabels = {
    today: t('client-card-today'),
    tomorrow: t('client-card-tomorrow'),
    yesterday: t('client-card-yesterday'),
  };

  const fullName = `${client.firstName} ${client.lastName}`.trim();
  const age = clientAge(client);
  const counts = recentVisitCounts(visits);

  const status = client.deceased
    ? { label: t('clients-field-deceased'), bg: '#f8fafc', fg: '#475569', bd: '#e2e8f0' }
    : client.active
      ? { label: t('clients-status-active'), bg: '#ecfdf5', fg: '#047857', bd: '#a7f3d0' }
      : { label: t('clients-status-inactive'), bg: '#f8fafc', fg: '#475569', bd: '#e2e8f0' };

  const identity = [
    age === null ? null : t('client-card-age-years', { count: age }),
    client.city,
    client.createdAt ? t('client-card-since', { date: formatDate(client.createdAt, i18n.language) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const nextLabel = next ? formatVisitWhen(next.startTime, i18n.language, relativeDayLabels) : '—';

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,.04)] lg:flex-row lg:items-center lg:gap-6">
      <div className="flex min-w-0 shrink-0 items-start gap-3 lg:w-[300px]">
        <AppAvatar isSelected fullName={fullName} value={getInitials(fullName)} size={44} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[17px] font-semibold leading-tight text-slate-900">{fullName}</span>
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{ background: status.bg, color: status.fg, border: `1px solid ${status.bd}` }}
            >
              {status.label}
            </span>
          </div>
          {identity && <div className="mt-0.5 text-[13px] leading-snug text-slate-500">{identity}</div>}
          {client.medicalHistory && <MedicalHistory text={client.medicalHistory} />}
        </div>
      </div>

      <div
        className={
          'grid min-w-0 flex-1 grid-cols-2 gap-4 lg:border-l lg:border-slate-100 lg:pl-6 ' +
          (canViewBilling ? 'sm:grid-cols-3' : 'sm:grid-cols-2')
        }
      >
        <button
          type="button"
          className="cursor-pointer border-0 bg-transparent p-0 text-left disabled:cursor-default"
          onClick={onOpenNext}
          disabled={!next}
        >
          <Figure
            label={t('client-card-next-visit')}
            value={nextLabel}
            sub={
              next
                ? visitServiceLines(next)
                    .map((line) => line.name)
                    .join(' · ') || undefined
                : t('client-card-no-upcoming-visit')
            }
          />
        </button>

        <Figure
          label={t('client-card-visits-in-window', { days: RECENT_WINDOW_DAYS })}
          value={counts.completed}
          sub={
            counts.cancelled > 0
              ? t('client-card-cancellations', { count: counts.cancelled })
              : t('client-card-no-cancellations')
          }
        />

        {/* Billing is hidden from non-admins across the whole app — the module's
            routes are not registered for them at all — so this is omitted rather
            than rendered as a figure that 403s and a drawer that leads to /404. */}
        {canViewBilling && (
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 text-left"
            onClick={onOpenBilling}
          >
            <Figure
              label={t('client-card-balance')}
              value={
                isBalanceLoading ? (
                  <Skeleton.Button active size="small" style={{ height: 22, width: 72 }} />
                ) : balance === null ? (
                  '—'
                ) : (
                  currencyFormatter(balance, undefined, i18n.language)
                )
              }
              sub={
                isBalanceLoading
                  ? undefined
                  : hasNoBillingAccount
                    ? t('client-card-no-billing-account')
                    : balance === null
                      ? t('client-card-balance-unavailable')
                      : balance > 0
                        ? t('balances-sign-guide-clients-positive')
                        : balance < 0
                          ? t('balances-sign-guide-clients-negative')
                          : undefined
              }
              tone={balance !== null && balance > 0 ? 'bad' : undefined}
            />
          </button>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {onNewRequest && (
          <Button type="primary" onClick={onNewRequest}>
            {t('client-new-request')}
          </Button>
        )}
        {onEdit && <Button onClick={onEdit}>{t('client-card-edit')}</Button>}
      </div>
    </div>
  );
};

export default ClientCardHeader;
